import ContactComparisonModal from "@/components/core/modals/ContactComparisonModal";
import {
  setCurrentTransactionField,
  setTransactionSignatures,
} from "@/state/transactions/CurrentTransactionEvents";
import { useTransactionSettings } from "@/state/transactions/TransactionSettingsHook";
import { InvitationMode } from "@/type/core/transaction/transaction.types";
import BasicButton from "@components/core/buttons/BasicButton";
import SubmitButton from "@components/core/buttons/SubmitButton";
import AddSignatoryForm from "@components/core/forms/AddSignatoryForm";
import ContactForm from "@components/core/forms/ContactForm";
import BasicModal from "@components/core/modals/BasicModal";
import InfoModal from "@components/core/modals/InfoModal";
import GenericSortableTable from "@components/core/tables/GenericSortableTable";
import ContactsIcon from "@static/core/icons/icon-contacts.svg?react";
import UserIcon from "@static/core/icons/icon-user.svg?react";
import { ActiveUser } from "@type/core/auth/auth.types";
import {
  Contact,
  Signatory,
  Signature,
} from "@type/core/dashboard/dashboard.types";
import { formValidateTriggers, MAX_SIGNATORIES } from "@utils/Constant";
import { SignatureScreenSectionProps } from "@views/core/extranet/transactions/signature/SignatureScreen";
import { columns } from "@views/core/extranet/transactions/signature/columns/SignatoriesColumns";
import useSignatoriesSection from "@views/core/extranet/transactions/signature/sections/SignatoriesSection/useSignatoriesSection";
import { Col, Form, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SignatoriesSectionProps extends SignatureScreenSectionProps {
  isFromModel?: boolean;
  className?: string;
  me?: ActiveUser;
  isFromDraft?: boolean;
  signatures?: Signature[];
}

const SignatoriesSection: FunctionComponent<SignatoriesSectionProps> = ({
  onSubmit,
  isFromModel = false,
  className = "",
  me,
  isFromDraft = false,
  signatures = [],
}) => {
  const { t } = useTranslation();
  const [form] = useForm();

  const {
    officeId,
    selectedOffice,
    signatories,
    setSignatories,
    experts,
    contacts,
    submit,
    originEditor,
    signatoryError,
    setSignatoryError,
    invitationMode: invitationModeFromDraft,
  } = useSignatoriesSection(me);

  const transactionSettings = useTransactionSettings();

  const signatureRight =
    selectedOffice &&
    selectedOffice.rights?.includes("SIGNATURE_COLLECTE_SIGNER");
  const isExpertComptable = selectedOffice && selectedOffice.expertComptable;
  const [invitationMode, setInvitationMode] = useState<InvitationMode>(
    invitationModeFromDraft,
  );
  const signaturesWithoutSignatory = isFromDraft
    ? signatures.filter((signature) => !signature.email && signature.rank === 1)
    : [];

  const maximumSignatoriesExceeded = signatories.length >= MAX_SIGNATORIES;

  const buttons = [
    {
      id: "me",
      label: t("signature.accordion.signatoryForm.addMe"),
      icon: <UserIcon />,
      disabled:
        maximumSignatoriesExceeded ||
        signatories.find((signatory) => signatory.email === me?.email) ||
        !me?.certificateInfos ||
        !me?.certificateInfos?.serialNumber,
      onClick: () => {
        setSignatoryError();
        const newSignatory: Signatory = {
          key: me?.email + Date.now().toString(),
          ...me,
          firstname: me?.firstname ?? "",
          lastname: me?.lastname ?? "",
          email: me?.email ?? "",
          office: selectedOffice,
        };
        setSignatories([...signatories, newSignatory]);
      },
    },
    {
      label: t("signature.accordion.signatoryForm.addExpert"),
      icon: <UserIcon />,
      onClick: () => {
        setSignatoryError();
        setIsAddExpertModalVisible(true);
      },
      disabled:
        maximumSignatoriesExceeded ||
        !experts.find(
          (expert) =>
            expert.officeId === officeId &&
            !signatories.find((signatory) => signatory.email === expert.email),
        ),
    },
    {
      label: t("signature.accordion.signatoryForm.addContact"),
      icon: <ContactsIcon />,
      onClick: () => {
        setSignatoryError();
        setIsAddContactModalVisible(true);
      },
      disabled: maximumSignatoriesExceeded,
    },
  ];

  const [fieldsSearch, setFieldsSearch] = useState<string>("");
  const [categoriesSearch, setCategoriesSearch] = useState<string[]>([]);
  const [isAddExpertModalVisible, setIsAddExpertModalVisible] =
    useState<boolean>(false);
  const [isAddContactModalVisible, setIsAddContactModalVisible] =
    useState<boolean>(false);
  const [
    isEmailAlreadyExistsModalVisible,
    setIsEmailAlreadyExistsModalVisible,
  ] = useState<boolean>(false);
  const [
    isContactAlreadyExistsModalVisible,
    setIsContactAlreadyExistsModalVisible,
  ] = useState<boolean>(false);
  const hasOfficeContactsDisabled = selectedOffice?.isContactDisabled;

  const [contactToAdd, setContactToAdd] = useState<Contact>();
  const [existingContact, setExistingContact] = useState<Contact>();

  useEffect(() => {
    if (isFromModel) {
      setCurrentTransactionField([
        {
          field: "invitationMode",
          value: invitationMode,
        },
      ]);
    }
  }, [invitationMode, isFromModel]);

  const handleSubmit = () => {
    setCurrentTransactionField([
      {
        field: "invitationMode",
        value: invitationMode,
      },
    ]);
    onSubmit?.();
    submit();
  };

  const handleSignatoryDelete = (signatoryEmail: string) => {
    const newSignatories: Signatory[] = signatories.filter(
      (signatory) => signatory.email !== signatoryEmail,
    );
    const newSignatures = signatures.filter(
      (signature) => signature.email !== signatoryEmail,
    );
    setSignatories(newSignatories);
    setTransactionSignatures(newSignatures);
  };

  return (
    <Form
      className={`app-section-signatories ${className}`}
      onFinish={handleSubmit}
      {...formValidateTriggers}
      layout="vertical"
    >
      {signatories && signatories.length > 0 ? (
        <GenericSortableTable<Signatory>
          data={signatories}
          setData={setSignatories}
          columns={columns(
            isExpertComptable,
            isFromModel,
            experts,
            handleSignatoryDelete,
            transactionSettings?.activateSignLevel2,
            originEditor,
          )}
          className="table-light-grey mb-16"
        />
      ) : (
        <div className="mb-16" />
      )}
      <ul>
        <li>{t("signature.accordion.signatoryForm.rule1")}</li>
        <li>{t("signature.accordion.signatoryForm.rule2")}</li>
      </ul>
      <span>{t("signature.accordion.signatoryForm.addSignatory")} :</span>
      <div className="d-flex align-items-center justify-content-center">
        <ul className="list-inline">
          {buttons.map((button, index) => {
            if (button.id !== "me" || isExpertComptable || signatureRight) {
              return (
                <li key={"button" + index} className="list-inline-item">
                  <BasicButton
                    variant="transparent-tertiary"
                    icon={button.icon}
                    text={button.label}
                    onClick={button.onClick}
                    disabled={!!button.disabled}
                  />
                </li>
              );
            } else {
              return null;
            }
          })}
        </ul>
      </div>
      {signaturesWithoutSignatory?.length > 0 && (
        <div>
          {t("signature.accordion.signatoryForm.supplementSignatories")}
          <ul>
            {signaturesWithoutSignatory.map((signature, signatureIndex) => (
              <li key={signatureIndex}>{signature.name}</li>
            ))}
          </ul>
        </div>
      )}
      {!isFromModel && signatoryError && (
        <div className="text-center text-danger">
          {t("signature.accordion.signatoryForm.signatoryError")}
        </div>
      )}
      <div className="d-flex align-items-center justify-content-end flex-wrap gap-8 pt-30">
        <div className="flex-grow-1 d-flex align-items-center flex-wrap gap-8">
          <BasicButton
            variant={invitationMode === "sequential" ? "primary" : "secondary"}
            className={`btn-rounded -mr-38 px-sm-48 ${
              invitationMode === "sequential" ? "z-2" : "z-1"
            }`}
            onClick={() => setInvitationMode("sequential")}
            text={t("signature.invitationMode.sequential")}
          />
          <BasicButton
            variant={invitationMode === "parallel" ? "primary" : "secondary"}
            className={`btn-rounded px-sm-48 ${
              invitationMode === "parallel" ? "z-2" : "z-1"
            }`}
            onClick={() => setInvitationMode("parallel")}
            text={t("signature.invitationMode.parallel")}
          />
          <InfoModal value="invitationMode" info className="ml-8" />
        </div>
        <SubmitButton
          disabled={!isFromModel && signatories.length === 0}
          label={isFromModel ? "buttons.next" : "buttons.validate"}
          className="mw-160"
        />
      </div>
      <BasicModal
        visible={isAddExpertModalVisible}
        onCancel={() => {
          setIsAddExpertModalVisible(false);
          form.resetFields();
          setFieldsSearch("");
        }}
        footer={null}
        title={t("signature.accordion.signatoryForm.addSignatories")}
      >
        <AddSignatoryForm
          baseContacts={experts}
          setSignatories={setSignatories}
          setIsModalVisible={setIsAddExpertModalVisible}
          signatories={signatories ? signatories : []}
          fromMembers
          form={form}
          fieldsSearch={fieldsSearch}
          setFieldsSearch={setFieldsSearch}
        />
      </BasicModal>
      <BasicModal
        visible={isAddContactModalVisible}
        onCancel={() => {
          setIsAddContactModalVisible(false);
          form.resetFields();
          setFieldsSearch("");
          setCategoriesSearch([]);
        }}
        footer={null}
        title={t("signature.accordion.signatoryForm.addSignatory")}
        className="modal-lg"
        /*Pour repartir d'un état initial pour les categories*/
        destroyOnClose={true}
      >
        <Row gutter={32}>
          <Col span={24} md={14} className="d-flex flex-column">
            <h2 className="text-primary py-24">
              {t("signature.accordion.signatoryForm.addFromContacts")}
              <InfoModal value="pickFromContacts" info />
              {!!hasOfficeContactsDisabled && (
                <h4 className="text-orange">
                  {t("signature.addContactModal.contactDisabled")}
                </h4>
              )}
            </h2>
            <AddSignatoryForm
              baseContacts={contacts}
              searchDisabled={!!hasOfficeContactsDisabled}
              setSignatories={setSignatories}
              setIsModalVisible={setIsAddContactModalVisible}
              signatories={signatories ? signatories : []}
              form={form}
              fieldsSearch={fieldsSearch}
              setFieldsSearch={setFieldsSearch}
              categoriesSearch={categoriesSearch}
              setCategoriesSearch={setCategoriesSearch}
            />
          </Col>
          <Col
            span={24}
            md={10}
            className="d-flex flex-column border-left-secondary"
          >
            <h2 className="text-primary py-24">
              {t("signature.accordion.signatoryForm.addNewSignatory")}
            </h2>
            <ContactForm
              isAddSignatory
              setIsEmailAlreadyExistsModalVisible={
                setIsEmailAlreadyExistsModalVisible
              }
              setSignatories={setSignatories}
              officeId={officeId}
              signatories={signatories}
              baseContacts={contacts}
              setIsContactAlreadyExistsModalVisible={
                setIsContactAlreadyExistsModalVisible
              }
              setContactToAdd={setContactToAdd}
              setExistingContact={setExistingContact}
              signatoryError={signatoryError}
              onSubmit={() => setIsAddContactModalVisible(false)}
            />
          </Col>
        </Row>
      </BasicModal>
      <BasicModal
        visible={isEmailAlreadyExistsModalVisible}
        onCancel={() => setIsEmailAlreadyExistsModalVisible(false)}
        footer={
          <BasicButton
            variant="primary"
            text={t("modals.info.dismiss")}
            onClick={() => setIsEmailAlreadyExistsModalVisible(false)}
            className="w-100"
          />
        }
        title={t("modal.warning")}
      >
        {t("signature.accordion.signatoryForm.emailAlreadyExists")}
      </BasicModal>
      {existingContact && contactToAdd && (
        <ContactComparisonModal
          visible={isContactAlreadyExistsModalVisible}
          onCancel={() => setIsContactAlreadyExistsModalVisible(false)}
          existingContact={existingContact}
          contactToAdd={contactToAdd}
          onContactChoose={(selectedContact) => {
            setSignatories([
              ...(signatories ?? []),
              {
                ...selectedContact,
                key: selectedContact.email + Date.now().toString(),
              },
            ]);
            setIsContactAlreadyExistsModalVisible(false);
          }}
        />
      )}
    </Form>
  );
};

export default SignatoriesSection;
